import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Wrapper,
  PhoneNumber,
  Title,
  SubTitle,
  ResendText,
  Content,
  ResendButton,
  OtpLoaderWrapper,
} from "./style";
import {
  FormWrapper,
  InputAdnLabelWrapper,
  PersonalInfoInput,
  PersonalInfoInputLabel,
} from "../verifyIdentity/styles";
import NavButton from "../../common/button";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getErrorMessage, handleIdentificationStepChange, redirectToPiOnboardingIdentification } from "../../helpers";
import { verifyOtp, resendOtp } from "../../../../../API/PIservices";
import { useMutation } from "@tanstack/react-query";
import Error from "../verifyIdentity/Modals/Error";
import useCountdown from "../../../hooks/useCountdown";
import { useNavigateAfterReload } from "../../../../../hooks/useNavigateAfterReload";
import Loader from "../../../../../shared/loaders/simpleLoader";
import PopupLoader from "../../../../../shared/loaders/PopupLoader";

function OTPVerification({ otpInfo }) {
  const history = useHistory();
  const [errorMessage, setErrorMessage] = useState(null);

  useNavigateAfterReload();

  const { t } = useTranslation();

  const { seconds, formatted, reset } = useCountdown(
    Number(otpInfo?.otpResendSeconds),
  );

  const isCountDownOver = Number(seconds) <= 0;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setFocus,
  } = useForm();

  const { isPending, mutate: handleVerifyOtp } = useMutation({
    mutationFn: (data) => {
      return verifyOtp(data);
    },
    onSuccess: () => {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set("step", "verification-method");

      history.replace({
        pathname: window.location.pathname,
        search: searchParams.toString(),
      });
    },

    onError: (err) => {
      setErrorMessage(getErrorMessage(err));
    },
  });

  const { isPending: isResendPending, mutate: resendOtpMutation } = useMutation(
    {
      mutationFn: (data) => {
        return resendOtp(data);
      },
      onSuccess: () => {
        reset();
        setValue("otpCode", "");
        setFocus("otpCode");
      },

      onError: (err) => {
        setErrorMessage(getErrorMessage(err));
      },
    },
  );

  useEffect(() => {
    if (!isCountDownOver && otpInfo?.otpResendSeconds) {
      setFocus("otpCode");
    }
  }, [isCountDownOver, otpInfo?.otpResendSeconds, setFocus]);

  function handleResend() {
    if (isPending) return;
    resendOtpMutation({});
  }

  function onSubmit(data) {
    if (isResendPending) return;
    handleVerifyOtp({
      otpCode: data?.otpCode,
    });
  }

  const isButtonDisabled = isPending || isResendPending || isCountDownOver;

  return (
    <Wrapper>
      <Content>
        <Title>{t("confirm_with_code")}</Title>

        <SubTitle>
          {t("code_has_been_sent")}{" "}
          <PhoneNumber>{otpInfo?.mobileNumberMask}</PhoneNumber>
        </SubTitle>

        <FormWrapper onSubmit={handleSubmit(onSubmit)}>
          <InputAdnLabelWrapper>
            <PersonalInfoInputLabel htmlFor="otpCode">
              {t("one_time_code")}
            </PersonalInfoInputLabel>
            <PersonalInfoInput
              disabled={
                isPending ||
                isResendPending ||
                isCountDownOver ||
                !otpInfo?.otpResendSeconds
              }
              inputmode="numeric"
              maxlength="6"
              pattern="^\d{1,6}$"
              {...register("otpCode", {
                required: true,
                minLength: {
                  value: 6,
                  message: "Must be at least 6 characters",
                },
              })}
              onInput={(e) => {
                const el = e.currentTarget;
                el.value = el.value.replace(/\D/g, "").slice(0, 6);
              }}
              hasError={errors?.otpCode}
            />
          </InputAdnLabelWrapper>

          {!otpInfo?.otpResendSeconds ? null : !isCountDownOver ? (
            <ResendText>
              {t("resend_code")} {formatted}
            </ResendText>
          ) : isResendPending ? (
            <OtpLoaderWrapper>
              <Loader className="otp-inline-loader" />
              <span>{t("sending") + "..."}</span>
            </OtpLoaderWrapper>
          ) : (
            <ResendButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleResend();
              }}
            >
              {t("resend_code")}
            </ResendButton>
          )}

          <NavButton
            disabled={isButtonDisabled}
            showPrevButton
            nextText={t("confirmation")}
            handlePrev={(e) => {
              e.stopPropagation();
              redirectToPiOnboardingIdentification();
            }}
          />
        </FormWrapper>
      </Content>
      <PopupLoader isOpen={isPending} />
      <Error
        errorMessage={errorMessage}
        close={() => {
          setErrorMessage(null);
        }}
      />{" "}
    </Wrapper>
  );
}

export default OTPVerification;
